import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { 
  Users, UserPlus, Trash2, KeyRound, Shield, ShieldCheck, 
  MapPin, Mail, RefreshCw, AlertTriangle, Eye, EyeOff, Edit
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchNames, setBranchNames] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Add User Modal
  const [addModal, setAddModal] = useState(false);
  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
    name: "",
    role: "inventory_associate",
    assigned_branches: []
  });
  const [showPassword, setShowPassword] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  
  // Edit User Modal - Change Role/Admin Status
  const [editModal, setEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    role: "inventory_associate",
    is_admin: false,
    assigned_branches: [],
    can_access_all_branches: false,
    // Permission flags
    can_view_payments: false,
    can_view_reports: true,
    can_view_analytics: true,
    can_delete_records: false
  });
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Reset Password Modal
  const [resetModal, setResetModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Delete Confirmation Modal
  const [deleteModal, setDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchBranches();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/auth/users`);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API}/inventory/branches`);
      setBranches(response.data.branches || []);
      setBranchNames(response.data.branch_names || {});
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const handleAddUser = async () => {
    // Validation
    if (!newUser.email || !newUser.password || !newUser.name) {
      toast.error("Pakikumpleto ang lahat ng required fields");
      return;
    }
    
    if (!newUser.email.endsWith("@orionledlights.com")) {
      toast.error("Email ay dapat nagtatapos sa @orionledlights.com");
      return;
    }
    
    if (newUser.password.length < 6) {
      toast.error("Password ay dapat at least 6 characters");
      return;
    }

    setAddingUser(true);
    try {
      await axios.post(`${API}/auth/register`, newUser);
      toast.success(`User ${newUser.name} ay successfully na-add!`);
      setAddModal(false);
      setNewUser({
        email: "",
        password: "",
        name: "",
        role: "inventory_associate",
        assigned_branches: []
      });
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to add user";
      toast.error(message);
    } finally {
      setAddingUser(false);
    }
  };

  const openResetModal = (user) => {
    setSelectedUser(user);
    setNewPassword("");
    setShowNewPassword(false);
    setResetModal(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password ay dapat at least 6 characters");
      return;
    }

    setResettingPassword(true);
    try {
      await axios.post(`${API}/auth/users/${selectedUser.id}/reset-password`, {
        password: newPassword
      });
      toast.success(`Password ni ${selectedUser.name} ay na-reset na!`);
      setResetModal(false);
      setNewPassword("");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to reset password";
      toast.error(message);
    } finally {
      setResettingPassword(false);
    }
  };

  const openDeleteModal = (user) => {
    setUserToDelete(user);
    setDeleteModal(true);
  };

  const handleDeleteUser = async () => {
    setDeletingUser(true);
    try {
      await axios.delete(`${API}/auth/users/${userToDelete.id}`);
      toast.success(`User ${userToDelete.name} ay na-delete na!`);
      setDeleteModal(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to delete user";
      toast.error(message);
    } finally {
      setDeletingUser(false);
    }
  };

  // Open Edit Modal
  const openEditModal = (user) => {
    setEditingUser(user);
    setEditForm({
      role: user.role || "inventory_associate",
      is_admin: user.is_admin || user.role === "admin",
      assigned_branches: user.assigned_branches?.length ? user.assigned_branches : (user.assigned_branch ? [user.assigned_branch] : []),
      can_access_all_branches: user.can_access_all_branches || user.is_admin || false,
      // Permission flags
      can_view_payments: user.can_view_payments || user.is_admin || false,
      can_view_reports: user.can_view_reports !== false,
      can_view_analytics: user.can_view_analytics !== false,
      can_delete_records: user.can_delete_records || user.is_admin || false
    });
    setEditModal(true);
  };

  // Handle Edit User - Update Role/Admin Status
  const handleEditUser = async () => {
    if (!editingUser) return;
    
    setSavingEdit(true);
    try {
      // Prepare update data
      const updateData = {
        role: editForm.is_admin ? "admin" : "inventory_associate",
        is_admin: editForm.is_admin,
        can_access_all_branches: editForm.is_admin || editForm.can_access_all_branches,
        assigned_branch: editForm.is_admin ? null : (editForm.assigned_branches[0] || null),
        assigned_branches: editForm.is_admin ? [] : editForm.assigned_branches,
        // Permission flags - admins get all permissions
        can_view_payments: editForm.is_admin || editForm.can_view_payments,
        can_view_reports: editForm.can_view_reports,
        can_view_analytics: editForm.can_view_analytics,
        can_delete_records: editForm.is_admin || editForm.can_delete_records
      };
      
      await axios.put(`${API}/auth/users/${editingUser.id}`, updateData);
      
      toast.success(
        <div>
          <div className="font-bold">✅ User Updated!</div>
          <div className="text-sm">{editingUser.name} permissions saved</div>
        </div>
      );
      
      setEditModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to update user";
      toast.error(message);
    } finally {
      setSavingEdit(false);
    }
  };

  const getRoleBadge = (role, isAdmin) => {
    if (role === "admin" || isAdmin) {
      return (
        <Badge className="bg-purple-100 text-purple-800 gap-1">
          <ShieldCheck className="w-3 h-3" />
          Admin
        </Badge>
      );
    }
    return (
      <Badge className="bg-blue-100 text-blue-800 gap-1">
        <Shield className="w-3 h-3" />
        Inventory Associate
      </Badge>
    );
  };

  // Check if current user is admin
  if (currentUser?.role !== "admin") {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="access-denied">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">
              Tanging Admin lamang ang makakapag-access sa User Management.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-500">Pamahalaan ang mga user accounts ng system</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchUsers} size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setAddModal(true)} data-testid="add-user-btn">
            <UserPlus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-none shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{users.length}</p>
                <p className="text-sm text-gray-500">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-none shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <ShieldCheck className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {users.filter(u => u.role === "admin").length}
                </p>
                <p className="text-sm text-gray-500">Admins</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-none shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <Shield className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {users.filter(u => u.role === "inventory_associate").length}
                </p>
                <p className="text-sm text-gray-500">Inventory Associates</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card className="border-none shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5" />
            All Users
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Walang users na nahanap</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="users-table">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Branch</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50" data-testid={`user-row-${user.id}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{user.name}</p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Mail className="w-4 h-4" />
                          {user.email}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {getRoleBadge(user.role, user.is_admin)}
                      </td>
                      <td className="px-4 py-3">
                        {(user.role === "admin" || user.is_admin) ? (
                          <span className="text-gray-400 text-sm">All Branches</span>
                        ) : (user.assigned_branches?.length || user.assigned_branch) ? (
                          <div className="flex items-center gap-1 flex-wrap" data-testid={`user-branches-${user.id}`}>
                            <MapPin className="w-4 h-4 text-gray-400" />
                            {(user.assigned_branches?.length ? user.assigned_branches : [user.assigned_branch]).map((b) => (
                              <Badge key={b} variant="outline" className="text-xs">
                                {branchNames[b] || b}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800 text-xs">Pending Assignment</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          {/* Edit Role Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(user)}
                            title="Edit Role / Make Admin"
                            data-testid={`edit-user-${user.id}`}
                            className="hover:bg-purple-50"
                          >
                            <ShieldCheck className="w-4 h-4 text-purple-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openResetModal(user)}
                            title="Reset Password"
                            data-testid={`reset-password-${user.id}`}
                          >
                            <KeyRound className="w-4 h-4 text-blue-600" />
                          </Button>
                          {user.email !== currentUser.email && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteModal(user)}
                              title="Delete User"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add User Modal */}
      <Dialog open={addModal} onOpenChange={setAddModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Add New User
            </DialogTitle>
            <DialogDescription>
              Mag-add ng bagong user sa system. Email ay dapat @orionledlights.com.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                placeholder="Juan Dela Cruz"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                data-testid="new-user-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Company Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="juan@orionledlights.com"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                data-testid="new-user-email"
              />
              <p className="text-xs text-gray-500">
                Dapat nagtatapos sa @orionledlights.com
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Minimum 6 characters"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  data-testid="new-user-password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Role *</Label>
              <Select 
                value={newUser.role} 
                onValueChange={(value) => setNewUser({ ...newUser, role: value })}
              >
                <SelectTrigger data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inventory_associate">Inventory Associate</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Branch Access (Optional - pwedeng multiple)</Label>
              <div className="grid grid-cols-2 gap-2 p-3 border rounded-lg bg-gray-50" data-testid="new-user-branches">
                {branches.map((branch) => (
                  <label key={branch} className="flex items-center gap-2 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={newUser.assigned_branches.includes(branch)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...newUser.assigned_branches, branch]
                          : newUser.assigned_branches.filter((b) => b !== branch);
                        setNewUser({ ...newUser, assigned_branches: next });
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      data-testid={`new-user-branch-${branch}`}
                    />
                    <span className="font-medium text-gray-900">{branchNames[branch] || branch}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500">
                I-check ang mga branches na pwedeng i-access. Kung wala, "Pending Assignment" muna ang user.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddUser} disabled={addingUser} data-testid="submit-add-user">
              {addingUser ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add User
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={resetModal} onOpenChange={setResetModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5" />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              I-reset ang password ni {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-600">User: <strong>{selectedUser?.name}</strong></p>
              <p className="text-sm text-gray-600">Email: {selectedUser?.email}</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password *</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Minimum 6 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setResetModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleResetPassword} disabled={resettingPassword} data-testid="submit-reset-password">
              {resettingPassword ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  <KeyRound className="w-4 h-4 mr-2" />
                  Reset Password
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModal} onOpenChange={setDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete User
            </DialogTitle>
            <DialogDescription>
              Sigurado ka bang gusto mong i-delete si {userToDelete?.name}?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">Warning!</p>
                  <p className="text-sm text-red-700 mt-1">
                    Ang user na ito ay permanenteng made-delete at hindi na makakapag-login sa system.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-4 bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-600">Name: <strong>{userToDelete?.name}</strong></p>
              <p className="text-sm text-gray-600">Email: {userToDelete?.email}</p>
              <p className="text-sm text-gray-600">Role: {userToDelete?.role}</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteUser} 
              disabled={deletingUser}
              data-testid="confirm-delete-user"
            >
              {deletingUser ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete User
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Role Modal - MAKE ADMIN */}
      <Dialog open={editModal} onOpenChange={setEditModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-purple-600" />
              Edit User Role
            </DialogTitle>
            <DialogDescription>
              I-update ang role at permissions ng user na ito.
            </DialogDescription>
          </DialogHeader>
          
          {editingUser && (
            <div className="space-y-4">
              {/* User Info */}
              <div className="p-3 bg-gray-50 rounded-lg border">
                <div className="font-semibold text-gray-900">{editingUser.name}</div>
                <div className="text-sm text-gray-500">{editingUser.email}</div>
              </div>

              {/* Admin Toggle - MAIN FEATURE */}
              <div className="p-4 border-2 rounded-xl bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${editForm.is_admin ? 'bg-purple-600' : 'bg-gray-300'}`}>
                      <ShieldCheck className={`w-6 h-6 ${editForm.is_admin ? 'text-white' : 'text-gray-500'}`} />
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">Admin Access</div>
                      <div className="text-sm text-gray-500">Full system access, all branches</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditForm({
                      ...editForm, 
                      is_admin: !editForm.is_admin,
                      can_access_all_branches: !editForm.is_admin
                    })}
                    className={`relative w-14 h-7 rounded-full transition-colors ${
                      editForm.is_admin ? 'bg-purple-600' : 'bg-gray-300'
                    }`}
                    data-testid="toggle-admin"
                  >
                    <span className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      editForm.is_admin ? 'translate-x-8' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>

              {/* Branch Assignment - Only show if NOT admin */}
              {!editForm.is_admin && (
                <div className="space-y-2">
                  <Label>Branch Access (pwedeng multiple)</Label>
                  <div className="grid grid-cols-2 gap-2 p-3 border rounded-lg bg-gray-50" data-testid="edit-branch-checkboxes">
                    {branches.map((branch) => (
                      <label key={branch} className="flex items-center gap-2 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={editForm.assigned_branches.includes(branch)}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...editForm.assigned_branches, branch]
                              : editForm.assigned_branches.filter((b) => b !== branch);
                            setEditForm({ ...editForm, assigned_branches: next });
                          }}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          data-testid={`edit-branch-${branch}`}
                        />
                        <span className="font-medium text-gray-900">{branchNames[branch] || branch}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">
                    I-check ang mga branches na pwedeng i-access ng user (hal. Pampanga Branch + OTG-B)
                  </p>
                </div>
              )}

              {/* Permission Checkboxes - Only show if NOT admin */}
              {!editForm.is_admin && (
                <div className="space-y-3 p-4 bg-gray-50 rounded-lg border">
                  <Label className="text-sm font-semibold text-gray-700">Permissions</Label>
                  
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input 
                        type="checkbox"
                        checked={editForm.can_view_payments}
                        onChange={(e) => setEditForm({...editForm, can_view_payments: e.target.checked})}
                        className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900">💰 View Payment/Collection Data</div>
                        <div className="text-xs text-gray-500">Makikita ang pera at collection tracker</div>
                      </div>
                    </label>

                    <label className="flex items-center gap-3 cursor-pointer">
                      <input 
                        type="checkbox"
                        checked={editForm.can_view_reports}
                        onChange={(e) => setEditForm({...editForm, can_view_reports: e.target.checked})}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900">📊 View Reports</div>
                        <div className="text-xs text-gray-500">Access to RTS reports and analytics</div>
                      </div>
                    </label>

                    <label className="flex items-center gap-3 cursor-pointer">
                      <input 
                        type="checkbox"
                        checked={editForm.can_delete_records}
                        onChange={(e) => setEditForm({...editForm, can_delete_records: e.target.checked})}
                        className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900">🗑️ Delete Records</div>
                        <div className="text-xs text-gray-500">Pwedeng mag-delete ng records</div>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* Current vs New Status */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Current Role:</span>
                    {getRoleBadge(editingUser.role, editingUser.is_admin)}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-gray-600">New Role:</span>
                    {editForm.is_admin ? (
                      <Badge className="bg-purple-100 text-purple-800 gap-1">
                        <ShieldCheck className="w-3 h-3" /> Admin
                      </Badge>
                    ) : (
                      <Badge className="bg-blue-100 text-blue-800 gap-1">
                        <Shield className="w-3 h-3" /> Inventory Associate
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Warning for demoting yourself */}
              {editingUser.email === currentUser.email && !editForm.is_admin && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm font-medium">Warning: Hindi mo pwedeng alisin ang sarili mong admin access!</span>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleEditUser} 
              disabled={savingEdit || (editingUser?.email === currentUser.email && !editForm.is_admin)}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="save-user-role"
            >
              {savingEdit ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
